<i18n>
{
  "en": {
    "title": "Sustainable Infrastructure Highlights 2017-2018",
    "description": "We help empower the sustainability efforts of utilities in the new world of energy in the face of unprecedented global electricity consumption. Learn about renewable energy sources and the changing green energy landscape. Read about our green industry solutions and South Australian Power Networks’ sustainable success story.",
    "ourCustomers": {
      "section": "Our Customers",
      "title": "Infrastructure",
      "infrastructure": {
        "highlights": {
          "title": "Sustainable infrastructure highlights",
          "para": "Our Infrastructure Business empowers utilities in the new world of energy and is paving the way for more electric, digitized, and distributed energy to power the digital economy. Currently, the generation of electricity is responsible for about 35 percent of carbon emissions in the United States alone<sup>1</sup>. Global energy consumption will increase by 40 percent in the next 25 years, and electricity consumption will increase by 80 percent<sup>2</sup>. Through connected products; edge control; and apps, analytics, and services, our proven EcoStruxure<sup>&trade;</sup> Grid architecture equips our electric utility customers for the changing energy landscape by:",
          "item0": "Improving resiliency and managing demand",
          "itemA": "Boosting grid efficiency",
          "itemB": "Integrating and managing distributed energy resources",
          "itemC": "Offering real-time situational awareness and control",
          "itemD": "Generating and protecting revenue",
          "paraB": "We are also reshaping energy landscapes by extracting maximum value from microgrid and helping utilities leverage exciting new prosumer trends. In the spirit of forward-thinking, last year, <a href='https://solar.schneider-electric.com/neoen-framework-agreement/' target='_blank'>Schneider signed a groundbreaking framework agreement with Neoen</a>, the premier French independent power producer, for 750 MW of solar solutions and services worldwide. The multi-year agreement touches three continents and includes:",
          "itemE": "Conversion stations composed of 1,500 V inverters",
          "itemF": "Medium-voltage switchgear",
          "itemG": "Transformers",
          "itemH": "Complete monitoring and control system",
          "itemI": "Life-cycle maintenance services",
          "paraC": "Neoen and Schneider Electric welcome this collaboration as a major joint initiative in providing more affordable and reliable clean energy. Energy management accounts for 76 percent of our sales, and through efforts including this and others like it, Infrastructure allows us to co-create solutions with electrical utilities that fit their needs and environments while reducing costs, and increasing operational excellence."
        },
        "table1": {
          "title": "A sample of our grid solutions",
          "row1": {
            "cell1": {
              "title": "Apps, Analytics & Services"
            },
            "cell3": {
              "title": "EcoStruxure ADMS",
              "p1": "Become a next-generation energy provider by enabling safer, more reliable, and efficient power management."
            }
          },
          "row2": {
            "cell1": {
              "title": "Edge Control"
            },
            "cell3": {
              "title": "EcoStruxure Substation Operation",
              "p1": "Enable re-thinking the design, operation, and maintenance of electrical installations."
            }
          },
          "row3": {
            "cell1": {
              "title": "Connected Products"
            },
            "cell3": {
              "title": "Easergy T300 remote terminal unit for feeder automation",
              "p1": "Control and monitor with a single solution, from a pole-top device to a large <span class='t-nowrap'>MV / MV</span> or <span class='t-nowrap'>MV / LV</span> substation."
            }
          }
        },
        "vid": {
          "title": "Reliability for South Australia Power Networks thanks to EcoStruxure",
          "description": "&nbsp;"
        },
        "successStory": {
          "title": "Reliability and grid efficiency success story: South Australian Power Networks",
          "subtitle": "South Australian Power Networks’ sustainability journey",
          "para": "South Australian Power Networks (SA) had a bold vision to keep electricity flowing for 1.5 million households through the most extreme storms. So, they turned to EcoStruxure for reliable monitoring and management of their extensive network, helping keep the people of South Australia safe — and powered — through extreme environmental conditions."
        },
        "table2": {
          "title": "SA Power Networks' sustainable solutions",
          "columnHeaders": {
            "left": {
              "title": "What"
            },
            "right": {
              "title": "Why"
            }
          },
          "row1": {
            "cell1": {
              "title": "Advanced distribution management system (ADMS)"
            },
            "cell2": {
              "p1": "Improves outage responses and optimizes evolving grid operation; critical grid management capabilities"
            }
          },
          "row2": {
            "cell1": {
              "title": "Decentralized feeder automation systems"
            },
            "cell2": {
              "p1": "Reduces the impact of outages and helps manage power restoration"
            }
          },
          "row3": {
            "cell1": {
              "title": "RM6 medium-voltage switchgear"
            },
            "cell2": {
              "p1": "Complete solution to enable the connection, supply, and protection of transformers"
            }
          },
          "row4": {
            "cell1": {
              "title": "MiCOM&trade; compact and modular substation and bay controller"
            },
            "cell2": {
              "p1": "Provides open automation processing in demanding electromagnetic environments"
            }
          }
        },
        "benefits": {
          "para": "Recorded benefits to date include:",
          "itemA": "Fault location, isolation, and service restoration",
          "itemB": "Network monitoring",
          "itemC": "Load-flow analysis",
          "itemD": "Real-time control and optimization",
          "itemE": "Operational planning",
          "itemF": "Training and simulation",
          "p1": "<strong>Get the facts on SA Power Networks’ challenges, sustainable solutions, and results.</strong>",
          "ctaText": "Learn more"
        },
        "references": {
          "one": "<sup>1</sup> Source: U.S. Energy Information Administration, 2017 FAQ",
          "two": "<sup>2</sup> Source: IEA, WEO 2014, New Policies Scenario"
        },
        "quote": {
          "para": "We have over 1.5 million customers. To be able to restore power to the majority of customers on a feeder in under a minute is something that has never been done in our history. Without Schneider Electric, we probably wouldn't have been able to progress as fast as we have.",
          "author": "Tasnim Abdel-Razaq",
          "title": "Network Control Manager, South Australia Power Networks"
        }
      }
    }
  },
  "fr": {
    "title": "Sustainable Infrastructure Highlights 2017-2018",
    "description": "We help empower the sustainability efforts of utilities in the new world of energy in the face of unprecedented global electricity consumption. Learn about renewable energy sources and the changing green energy landscape. Read about our green industry solutions and South Australian Power Networks’ sustainable success story.",
    "ourCustomers": {
      "section": "Nos Clients",
      "title": "Infrastructure",
      "infrastructure": {
        "highlights": {
          "title": "Faits marquants infrastructures durables",
          "para": "Notre activité pour le marché de l'infrastructure réseaux donne aux services de distribution d'électricité les moyens de prospérer dans ce nouvel univers et ouvre la voie à une distribution d’énergie plus électrique, plus numérique et plus décentralisée, qui alimente l’économie digitale. Aujourd'hui, environ 35 % des émissions de CO<sub>2</sub> sont générées par l’électricité <sup>1</sup> aux Etats-Unis. Dans les 25 prochaines années, la consommation mondiale d'énergie augmentera de 40 % et la consommation d’électricité de 80 % <sup>2</sup>. Grâce à ses produits connectés, à ses solutions de contrôle local, et à ses applications, outils d’analyse et services, notre solution éprouvée EcoStruxure™ donne à nos clients distributeurs les outils pour répondre à la demande croissante d'énergie :",
          "item0": "Amélioration de la résilience et de la gestion de la demande",
          "itemA": "Assurance d'une plus grande efficacité du réseau",
          "itemB": "Intégration et gestion de la distribution des ressources énergétiques ",
          "itemC": "Contrôle de la situation en temps réel",
          "itemD": "Production et protection des revenus",
          "paraB": "Nous avançons également dans la réorganisation du paysage de l’énergie, en capitalisant sur la valeur des microgrids et en anticipant les tendances liées aux prosommateurs. Dans un esprit pionnier, Schneider Electric a signé l’an dernier un accord-cadre avec Neoen, premier producteur indépendant d'électricité français, pour déployer 750 MW de solutions et de services d'énergie solaire à travers le monde. Cet accord pluriannuel porte sur trois continents et comprend la fourniture de :",
          "itemE": "Stations de conversion composées d'onduleurs 1 500 V",
          "itemF": "Disjoncteurs moyenne tension",
          "itemG": "Transformateurs",
          "itemH": "Systèmes complets de supervision et de contrôle",
          "itemI": "Services de maintenance et de gestion du cycle de vie",
          "paraC": "Neoen et Schneider Electric considèrent cette collaboration comme une initiative commune majeure pour proposer une énergie plus verte, plus fiable et plus abordable. La gestion de l’énergie représente 76 % de nos ventes. À travers des efforts de ce type, nous co-créons avec les services de distribution d'électricité des solutions répondant à leurs besoins et adaptées à leur contexte, qui leur permettent de réduire les coûts et de renforcer leur excellence opérationnelle."
        },
        "table1": {
          "title": "Exemples de solutions pour les réseaux",
          "row1": {
            "cell1": {
              "title": "Applications, outils d'analyse et services"
            },
            "cell3": {
              "title": "EcoStruxure ADMS",
              "p1": "Devenir la prochaine génération de fournisseur d'énergie en permettant une gestion de l'énergie plus sécurisée, plus fiable et plus efficace."
            }
          },
          "row2": {
            "cell1": {
              "title": "Contrôle local (Edge control)"
            },
            "cell3": {
              "title": "EcoStruxure Substation Operation",
              "p1": "Permettre de repenser la conception, le fonctionnement et la maintenance des installations électriques."
            }
          },
          "row3": {
            "cell1": {
              "title": "Produits connectés"
            },
            "cell3": {
              "title": "Unité de télégestion Easergy T300",
              "p1": "Gérer et automatiser avec une solution unique aussi bien un produit en hauteur qu' une large sous-station MT/MT ou MT/HT"
            }
          }
        },
        "vid": {
          "title": "Grâce à EcoStruxure, la fiabilité est au rendez-vous pour Australia Power Networks",
          "description": "&nbsp;"
        },
        "successStory": {
          "title": "Fiabilité et efficacité du réseau pour South Australian Power Networks",
          "subtitle": "South Australian Power Networks et le développement durable",
          "para": "South Australian Power Networks (SA Power Networks) s’est fixé un objectif ambitieux : fournir de l’électricité à 1,5 million de foyers, même lorsque les tempêtes les plus extrêmes se déchaînent. L’entreprise s’est donc tournée vers EcoStruxure™ pour assurer le contrôle et la gestion fiable de son vaste réseau, permettant ainsi aux habitants d’Australie-Méridionale de rester en sécurité et connectés au réseau, y compris dans les conditions les plus extrêmes."
        },
        "table2": {
          "title": "Solutions durables :",
          "columnHeaders": {
            "left": {
              "title": "Solution"
            },
            "right": {
              "title": "Avantages"
            }
          },
          "row1": {
            "cell1": {
              "title": "Système de gestion de distribution avancé (ADMS) "
            },
            "cell2": {
              "p1": "Amélioration de la gestion des coupures et optimisation de l’exploitation fluctuante du réseau ; capacités de gestion du réseau critique"
            }
          },
          "row2": {
            "cell1": {
              "title": "Dispositifs d’automatisation décentralisés"
            },
            "cell2": {
              "p1": "Réduction de l’impact des coupures et contribution au rétablissement de l’alimentation électrique"
            }
          },
          "row3": {
            "cell1": {
              "title": "Disjoncteur moyenne tension RM6"
            },
            "cell2": {
              "p1": "Solution complète permettant le raccordement, l'approvisionnement et la protection des transformateurs"
            }
          },
          "row4": {
            "cell1": {
              "title": "Équipements modulaires et compacts MiCOM™"
            },
            "cell2": {
              "p1": "Solution d'automatisation ouverte dans des environnements électromagnétiques exigeants"
            }
          }
        },
        "benefits": {
          "para": "Résultats obtenus à ce jour :",
          "itemA": "Localisation des pannes, isolation et restauration des services",
          "itemB": "Surveillance du réseau",
          "itemC": "Analyse du flux de charge",
          "itemD": "Contrôle et optimisation en temps réel",
          "itemE": "Planification opérationnelle",
          "itemF": "Formation et simulation",
          "p1": "<strong>Pour en savoir plus sur les défis, les solutions durables et les résultats de SA Power Networks, cliquez ici</strong>",
          "ctaText": "En savoir plus"
        },
        "references": {
          "one": "<sup>1</sup> Source: U.S. Energy Information Administration, 2017 FAQ",
          "two": "<sup>2</sup> Source: AIE, WEO 2014, scénario « Nouvelles politiques »"
        },
        "quote": {
          "para": "Nous avons plus de 1,5 million de clients. Pour rétablir l'électricité à la majorité d'entre eux en moins d'une minute, c'est quelque chose qui n'est jamais survenu dans l'histoire de notre entreprise. Sans Schneider Electric, nous n'aurions jamais pu progresser aussi vite que nous l'avons fait.",
          "author": "Tasnim Abdel-Razaq",
          "title": "Responsable Contrôle des réseaux, South Australia Power Networks"
        }
      }
    }
  }
}
</i18n>
<template>
  <div id="">
    <Hero :loResSrc="require('@/assets/img/our-customers/our-customers-infrastructure-hero-lowres.jpg')" :hiResSrc="require('@/assets/img/our-customers/our-customers-infrastructure-hero.jpg')" chapter="03" :section="$t('ourCustomers.section')" :title="$t('ourCustomers.title')"/>

    <section class="m-b-120 m-t-120">
      <Txt>
        <h2 v-html="$t('ourCustomers.infrastructure.highlights.title')" />
        <p id="infrastructure" v-html="$t('ourCustomers.infrastructure.highlights.para')" />
        <ul>
          <li v-html="$t('ourCustomers.infrastructure.highlights.item0')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemA')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemB')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemC')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemD')" />
        </ul>
        <p v-html="$t('ourCustomers.infrastructure.highlights.paraB')" />
        <ul>
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemE')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemF')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemG')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemH')" />
          <li v-html="$t('ourCustomers.infrastructure.highlights.itemI')" />
        </ul>
        <p id="neoen" v-html="$t('ourCustomers.infrastructure.highlights.paraC')" />
      </Txt>
    </section>

    <section class="l-bg-white l-section-pad">
      <Table columns="3">
        <h2 id="gridsolutions" slot="above" v-html="$t('ourCustomers.infrastructure.table1.title')"></h2>
        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table1.row1.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/our-customers/EcoStruxure-ADMS.jpg')" class="no-overlay contain-img">
          </TableCell>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table1.row1.cell3.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table1.row1.cell3.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 class="t-green-light" v-html="$t('ourCustomers.infrastructure.table1.row2.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/our-customers/our-customers-infrastructure-row2.jpg')" class="no-overlay contain-img">
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table1.row2.cell3.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table1.row2.cell3.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table1.row3.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/our-customers/our-customers-infrastructure-row3.jpg')" class="no-overlay contain-img">
          </TableCell>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table1.row3.cell3.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table1.row3.cell3.p1')"></p>
          </TableCell>
        </TableRow>
      </Table>
    </section>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo class="m-b-80" videoId="Xd5a_qjd-u4" :title="$t('ourCustomers.infrastructure.vid.title')" :description="$t('ourCustomers.infrastructure.vid.description')" :preview="require('@/assets/img/thumbnails/SAPN.jpg')" />

      <TxtMedia>
        <h2 class="m-b-45" slot="main-content" v-html="$t('ourCustomers.infrastructure.successStory.title')"/>
        <h3 id="sapnstory" class="m-b-15" slot="main-content" v-html="$t('ourCustomers.infrastructure.successStory.subtitle')"/>
        <p slot="main-content" v-html="$t('ourCustomers.infrastructure.successStory.para')" />
        <img slot="sidebar" src="@/assets/img/our-customers/our-customers-infrastructure.jpg" />
      </TxtMedia>
    </section>

    <section class="l-bg-white l-section-pad m-b-50">
      <Table class="m-b-50" columns="2">
        <h2 id="sapnsolutions" slot="above" v-html="$t('ourCustomers.infrastructure.table2.title')"/>
        <TableRow>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.left.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row1.cell1.title')"></p>
          </TableCell>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.right.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row1.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.left.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row2.cell1.title')"></p>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.right.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row2.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.left.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row3.cell1.title')"></p>
          </TableCell>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.right.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row3.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.left.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row4.cell1.title')"></p>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('ourCustomers.infrastructure.table2.columnHeaders.right.title')"></h3>
            <p class="t-chartbody" v-html="$t('ourCustomers.infrastructure.table2.row4.cell2.p1')"></p>
          </TableCell>
        </TableRow>
      </Table>

      <Txt class="">
        <h3 v-html="$t('ourCustomers.infrastructure.benefits.para')"/>
        <div class="m-b-32">
          <ul class="">
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemA')"/>
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemB')"/>
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemC')"/>
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemD')"/>
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemE')"/>
            <li v-html="$t('ourCustomers.infrastructure.benefits.itemF')"/>
          </ul>
        </div>
      </Txt>
      <Quote class="m-b-50" :photo="require('@/assets/img/our-customers/Tasnim-Abdel-Razaq_SAPN.jpg')" :name="$t('ourCustomers.infrastructure.quote.author')" :title="$t('ourCustomers.infrastructure.quote.title')">
        <div v-html="$t('ourCustomers.infrastructure.quote.para')"></div>
      </Quote>

      <Txt>
        <p v-html="$t('ourCustomers.infrastructure.benefits.p1')" class="t-grey"></p>
        <CTA target="_blank" :buttonText="$t('ourCustomers.infrastructure.benefits.ctaText')" href="https://www.schneider-electric.com/en/work/campaign/life-is-on/case-study/sa-power-networks.jsp"></CTA>
      </Txt>
    </section>

    <!-- <section class="m-b-120 m-t-120">
      <Statement class="m-b-30">
        <h3 v-html="$t('ourCustomers.infrastructure.statement')" />
      </Statement>
    </section> -->

    <!-- <hr> -->

    <References class="m-b-30">
      <Reference v-html="$t('ourCustomers.infrastructure.references.one')"/>
      <Reference v-html="$t('ourCustomers.infrastructure.references.two')"/>
    </References>

    <div class="l-content">
      <FooterNav
        nextRoute="OurPeople_Overview"
        prevRoute="OurCustomers_Industry" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import TxtMedia from '@/components/TxtMedia'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Txt from '@/components/Txt'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import ImageFull from '@/components/ImageFull'
import Reference from '@/components/Reference'
import References from '@/components/References'
import YouTubeVideo from '@/components/YouTubeVideo'

export default {
  name: 'OurCustomers_Infrastructure',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    TxtMedia,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Txt,
    Table,
    TableRow,
    TableCell,
    ImageFull,
    Reference,
    References,
    YouTubeVideo
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/our-customers/infrastructure.vue